import * as _ from 'lodash';
import States from 'login-constants/states';

const lenderName =
  _.escape(window.DEPLOYMENT_CONFIG.organization.name) || /* istanbul ignore next: (UI-5482) */ 'corporate';

export interface MessageInfoType {
  name: string;
  type: MessageType;
  message: string;
}

const LOGIN_MESSAGE_CONFIG: Readonly<{ [key: string]: MessageInfoType }> = {
  TIMED_OUT: {
    name: 'timedOut',
    type: 'error',
    message: `You've been logged out of your account due to inactivity.`,
  },
  ACTIVATION_REQUIRED: {
    name: 'activationRequired',
    type: 'error',
    message: `This account hasn't been activated yet. Please check your email for your activation link.`,
  },
  LOGIN_NOT_ALLOWED: {
    name: 'notAllowed',
    type: 'error',
    message: 'This login method is not enabled for your account.',
  },
  PASSWORD_EXPIRED: {
    name: 'passwordExpired',
    type: 'error',
    message:
      'It has been more than 90 days since you last updated your password. ' +
      'Please enter your email to update your password.',
  },
  LOGIN_FAILED: {
    name: 'loginFailed',
    type: 'error',
    message:
      'Incorrect email or password. After 3 failed attempts, the account ' +
      `will be locked and you will have to <a href="${States.REQUEST_RESET_PASSWORD}">reset your password</a> to access your account.`,
  },
  ALREADY_ACTIVATED: {
    name: 'alreadyActivated',
    type: 'error', // TODO: should this be success?
    message: 'You have already activated your account. Please log in with your username and password.',
  },
  INACTIVE_ACCOUNT: {
    name: 'inactiveAccount',
    type: 'error',
    message: 'Your account has been deactivated for inactivity. Please sign up for a new account.',
  },
  CHANGED_PASSWORD: {
    name: 'changedPassword',
    type: 'success',
    message: 'Your password has been successfully changed.',
  },
  INVALID_IP: {
    name: 'invalidIP',
    type: 'error',
    message: `Please connect to a ${lenderName} network or VPN to login.`,
  },
  ACTIVATION_INVALID: {
    name: 'activationInvalid',
    type: 'error',
    message: 'The activation key is invalid or has already been used.',
  },
  ACTIVATION_EXPIRED: {
    name: 'activationExpired',
    type: 'error',
    message: 'The activation key is invalid or has already been used.',
  },
  FORCED_LOGOUT: {
    name: 'forceLogin',
    type: 'error',
    message: 'Please login to your account.',
  },
  MULTI_SESSION: {
    name: 'multiSessionLogout',
    type: 'error',
    message: "You've been logged out because your username has logged in elsewhere.",
  },

  DORMANCY_RESET_SENT: {
    name: 'dormancyResetSent',
    type: 'error',
    message:
      'Your account is locked because it has been dormant for more than 30 days. An email has been sent to your ' +
      'email address with a link to unlock your account.',
  },
  DORMANCY_RESET_REQUIRED: {
    name: 'dormancyResetRequired',
    type: 'error',
    message:
      'Your account is locked because it has been dormant for more than 30 days. Please contact your user ' +
      'administrator to have access restored.',
  },
  DORMANCY_RESET_EXPIRED: {
    name: 'dormancyResetExpired',
    type: 'error',
    message: 'The account reactivation request could not be found or has expired. Please request another if needed.',
  },
  DORMANCY_RESET_FAILED: {
    name: 'dormancyResetFailed',
    type: 'error',
    message: 'Sorry, something went wrong while reactivating your account. Please try again.',
  },
  DORMANCY_RESET_SUCCESSFUL: {
    name: 'dormancyResetSuccessful',
    type: 'success',
    message: 'We successfully reactivated your account. Please log in.',
  },
  OAUTH_ERROR: {
    name: 'oauthError',
    type: 'error',
    message: 'An OAuth authorization error has occurred.',
  },
};

export const LOGIN_MESSAGE_CODES: Readonly<any> = _.mapValues(LOGIN_MESSAGE_CONFIG, config => config.name);

export const SIGNUP_MESSAGES: Readonly<any> = {
  INVALID_TEMPLATE: 'Please select your desired loan type',
};

/**
 * Generate an error/login message to display to the user.
 * Important: Never display raw user input from the query para
 * @param messageCode {string}
 * @returns {MessageInfoType}
 */
export function generate(messageCode: string): MessageInfoType | null {
  if (!messageCode) {
    return null;
  }
  return _.find(LOGIN_MESSAGE_CONFIG, { name: messageCode }) || null;
}

export const POSSIBLE_ERROR_MESSAGES = _.values(_.mapValues(LOGIN_MESSAGE_CONFIG, config => config.message));
